import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";

const WinzRatesService = (function () {
    // Private data cache
    let data = [];
    let isListenerRegistered = false;
    let isLoading = false; // Add loading flag to prevent duplicate requests
    let loadingPromise = null; // Store the current loading promise

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function updateWinzRate(updatedRecord) {
        try {
            if (!updatedRecord || !updatedRecord.id) {
                logger.warn('Invalid winz rate record received for update:', updatedRecord);
                return;
            }

            const existingRecordIndex = data.findIndex(record => record.id === updatedRecord.id);

            if (existingRecordIndex !== -1) {
                // Update existing record in place to maintain array references
                data[existingRecordIndex] = { ...data[existingRecordIndex], ...updatedRecord };
                logger.debug('Updated existing winz rate record:', updatedRecord.id);
            } else {
                // Add new record
                data.push(updatedRecord);
                logger.debug('Added new winz rate record:', updatedRecord.id);
            }

            Eventbus.emit(events.WINZ_RATES_UPDATED, [...data]); // Emit updated data
        } catch (error) {
            logger.error('Error updating winz rate record:', error, updatedRecord);
        }
    }

    function loadData(forceRefresh = false) {
        // If already loading and not forcing refresh, return the existing promise
        if (isLoading && !forceRefresh && loadingPromise) {
            return loadingPromise;
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            Eventbus.emit(events.WINZ_RATES_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        isLoading = true;

        // Create and store the loading promise
        loadingPromise = api
            .getWinzRates()
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return data; // Return existing data on cancellation
                }
                data = response || []; // Store the response data in the data variable
                Eventbus.emit(events.WINZ_RATES_LOADED, [...data]);

                // Only register the event listener once
                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.WINZ_RATES_DATA_CHANGES, updateWinzRate);
                    isListenerRegistered = true;
                    logger.debug('SignalR winz rates data changes listener registered');
                }

                return [...data]; // Return a copy of the data
            })
            .catch((error) => {
                logger.error('Error fetching winz rate records:', error);
                return [];
            })
            .finally(() => {
                isLoading = false;
                loadingPromise = null; // Clear the promise reference
            });

        return loadingPromise;
    }

    function init() {
        return loadData(true);
    }

    function clear() {
        if (isListenerRegistered) {
            Eventbus.off(updateWinzRate);
            isListenerRegistered = false;
            logger.debug('SignalR winz rates data changes listener removed');
        }
        data = [];
        isLoading = false;
        loadingPromise = null;
    }

    function updateWinzRateRecord(id, updatedData) {
        const existingRecord = data.find(record => record.id === id);
        if (!existingRecord) {
            logger.warn('Winz rate record not found for update:', id);
            return Promise.reject(new Error('Winz rate record not found'));
        }
        return api.updateWinzRate({ ...existingRecord, ...updatedData })
            .then((response) => {
                Eventbus.emit(events.SIGNALR.WINZ_RATES_DATA_CHANGES, response);
                return response;
            })
            .catch((error) => {
                logger.error('Error updating winz rate:', error);
                throw error;
            });
    } 
    
    function deleteWinzRate(id) {
        return api.deleteWinzRate(id)
            .then((response) => {
                // Remove from local cache immediately
                const index = data.findIndex(record => record.id === id);
                if (index !== -1) {
                    data.splice(index, 1);
                    Eventbus.emit(events.WINZ_RATES_UPDATED, [...data]);
                }
                return response;
            })
            .catch((error) => {
                logger.error('Error deleting winz rate:', error);
                throw error;
            });
    }

    function createWinzRate(rateData) {
        if (!rateData) {
            logger.warn('Invalid winz rate data for creation:', rateData);
            return Promise.reject(new Error('Invalid winz rate data'));
        }
        logger.debug('Creating winz rate:', rateData);
        return api.updateWinzRate(rateData) // Using updateWinzRate as it's an upsert
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    logger.warn('Winz rate creation cancelled:', rateData);
                    return response;
                }
                // Optionally, update local cache and emit event
                data.push(response);
                Eventbus.emit(events.WINZ_RATES_UPDATED, [...data]);
                logger.debug('Winz rate created successfully:', response);
                return response;
            })
            .catch((error) => {
                logger.error('Error creating winz rate:', error, rateData);
                throw error;
            });
    }

    function getLoadingState() {
        return isLoading;
    }

    return {
        getData,
        loadData,
        init,
        clear,
        updateWinzRate, // For SignalR updates
        updateWinzRateRecord,
        deleteWinzRate,
        getLoadingState, // Export loading state
        createWinzRate, // Export createWinzRate
    };
})();

export default WinzRatesService;
